import { type CreateLabelRequest, LabelType } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useCreateLabelMutation } from '../../../graphql/hooks/useCreateLabelMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { LabelForm } from './LabelForm';

interface Props {
  type: LabelType;
}

type FormValues = CreateLabelRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  type: z.enum(LabelType),
  name: z.string(),
  color: z.string(),
  description: z.string().nullish(),
});

export const LabelCreateModal = createModal<Props>((props) => {
  const { type, modal } = props;
  const { hide, remove, visible } = modal;
  const { errorToast, successToast, organization } = useAppContext();

  const initialValues: FormValues = {
    type,
    name: '',
    color: 'blue',
    description: '',
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useCreateLabelMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      await mutation
        .mutateAsync({
          organizationId: organization.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Label created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating label: ${err.message}`);
        });
    },
    [mutation, organization.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a label'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <LabelForm />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
